import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import CreditsTopupSheet from "@/components/CreditsTopupSheet";
import Landing from "@/pages/Landing";
import Chat from "@/pages/Chat";
import KalkiV2 from "@/pages/KalkiV2";
import AuthCallback from "@/pages/AuthCallback";
import HomeLanding from "@/pages/HomeLanding";
import DivineLanding from "@/pages/DivineLanding";
import LifeReport from "@/pages/LifeReport";
import TalkKrishna from "@/pages/TalkKrishna";
import ChatKrishna from "@/pages/ChatKrishna";
import VoiceAudition from "@/pages/VoiceAudition";
import TalkDiagnostics from "@/pages/TalkDiagnostics";
import VoiceProfileAB from "@/pages/VoiceProfileAB";
import YourBeginning from "@/pages/YourBeginning";
import DeepenProfile from "@/pages/DeepenProfile";
import ReferralLanding from "@/pages/ReferralLanding";
import InnerMirrorApp from "@/pages/inner_mirror/InnerMirrorApp";
import InnerMirrorAck from "@/pages/inner_mirror/InnerMirrorAck";
import AdminMetrics from "@/pages/AdminMetrics";
import AdminApp from "@/pages/admin/AdminApp";
import Temple from "@/pages/Temple";
import MyJourneyPage from "@/pages/MyJourneyPage";
import FuturePage from "@/pages/FuturePage";
import OnboardingGuard from "@/components/kalki/OnboardingGuard";
import CouponWelcomePopup from "@/components/kalki/CouponWelcomePopup";
import BlessingPopup from "@/components/kalki/BlessingPopup";
import LifeReportResumeToast from "@/components/kalki/LifeReportResumeToast";
import useOverflowAudit from "@/lib/useOverflowAudit";

function Guarded({ children }) {
  return <OnboardingGuard>{children}</OnboardingGuard>;
}

function AppRouter() {
  const location = useLocation();
  // Dev-only: scan every route for elements that extend past the
  // viewport width.  Dead-code-eliminated in production builds.
  useOverflowAudit();
  // Synchronous session_id capture — must handle BEFORE normal routing to avoid race.
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <>
      <Routes>
        <Route path="/" element={<DivineLanding />} />
        <Route path="/legacy-home" element={<HomeLanding />} />
        <Route path="/app" element={<Guarded><KalkiV2 /></Guarded>} />
        <Route path="/app/report" element={<Guarded><LifeReport /></Guarded>} />
        <Route path="/app/talk" element={<Guarded><TalkKrishna /></Guarded>} />
        <Route path="/app/talk/voices" element={<Guarded><VoiceAudition /></Guarded>} />
        <Route path="/app/talk/voices/ab" element={<Guarded><VoiceProfileAB /></Guarded>} />
        <Route path="/app/talk/diagnostics" element={<Guarded><TalkDiagnostics /></Guarded>} />
        <Route path="/app/chat" element={<Guarded><ChatKrishna /></Guarded>} />
        <Route path="/app/temple" element={<Guarded><Temple /></Guarded>} />
        <Route path="/app/journey" element={<Guarded><MyJourneyPage /></Guarded>} />
        <Route path="/app/future" element={<Guarded><FuturePage /></Guarded>} />
        <Route path="/onboarding/your-beginning" element={<YourBeginning />} />
        <Route path="/onboarding/deepen" element={<DeepenProfile />} />
        <Route path="/r/:code" element={<ReferralLanding />} />
        <Route path="/inner-mirror" element={<InnerMirrorApp />} />
        <Route path="/inner-mirror/ack/:token" element={<InnerMirrorAck />} />
        <Route path="/admin/metrics" element={<AdminMetrics />} />
        <Route path="/admin/*" element={<AdminApp />} />
        <Route path="/v2" element={<Guarded><KalkiV2 /></Guarded>} />
        <Route path="/legacy" element={<Landing />} />
        <Route path="/chat" element={<Guarded><Chat /></Guarded>} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      {/* KALKI 7.0 — global "preparing your Life Report" indicator.
          Mounted INSIDE Router so it can useNavigate(). */}
      <LifeReportResumeToast />
    </>
  );
}

function AdminAwarePopups() {
  // Suppress consumer upsell popups on /admin* routes so the Admin
  // Dashboard lands clean (iter_54 + iter_55 deferred UX nit).
  const location = useLocation();
  if ((location.pathname || "").startsWith("/admin")) return null;
  return (
    <>
      <CouponWelcomePopup />
      <BlessingPopup />
    </>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AppRouter />
        {/* Sprint C — global UPI top-up sheet, opens on any 402 response. */}
        <CreditsTopupSheet />
        {/* v0.6 Phase A — one-time welcome coupon gift (non-admin only) */}
        {/* v0.6 Phase 4 — Krishna Blessings reward popup (non-admin only) */}
        <AdminAwarePopups />
      </BrowserRouter>
      <Toaster
        position="top-center"
        toastOptions={{
          style: {
            background: "rgba(10,15,40,0.9)", color: "#F8FAFC",
            border: "1px solid rgba(223,185,110,0.4)", borderRadius: "9999px",
            boxShadow: "0 10px 30px rgba(0,0,0,0.4)",
            fontFamily: "'Inter', sans-serif", backdropFilter: "blur(12px)",
          },
        }}
      />
    </div>
  );
}

export default App;
